import React, { useState, useEffect } from 'react';
import { FormGroup, Label, Input } from 'reactstrap';
import styled from 'styled-components';

const FormBox = styled.div`
  border: 2px solid #20c997;
  border-radius: 8px;
  padding: 20px;
  background: linear-gradient(135deg, #d4edda, #ffffff);
  margin-bottom: 20px;

  h5 {
    color: #20c997;
    margin-bottom: 15px;
    font-weight: 600;
  }
`;

const PassageSection = styled.div`
  background: #e7f5ec;
  border: 1px solid #20c997;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 15px;
  max-height: 200px;
  overflow-y: auto;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.6;
`;

const QuestionCard = styled.div`
  border: 2px solid #20c997;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
`;

const OptionRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }
`;

const OptionBadge = styled.span`
  background: #20c997;
  color: white;
  padding: 4px 10px;
  border-radius: 4px;
  font-weight: 600;
  min-width: 35px;
  text-align: center;
`;

const RemoveButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background: #c82333;
  }
`;

const AddButton = styled.button`
  background: #20c997;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 15px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 10px;
  
  &:hover {
    background: #1aa179;
  }
`;

const RemoveOptionButton = styled.button`
  background: #ffc107;
  color: #333;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 11px;
  
  &:hover {
    background: #e0a800;
  }
`;

const AddOptionButton = styled.button`
  background: #17a2b8;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 12px;
  margin-left: 8px;
  
  &:hover {
    background: #138496;
  }
`;

interface MCQuestion {
  id: string;
  question_text: string;
  options: string[];
  correct_index: number;
}

interface ReadingMCQFormProps {
  formData: any;
  onChange: (data: any) => void;
}

const ReadingMCQForm: React.FC<ReadingMCQFormProps> = ({ formData, onChange }) => {
  const parseContent = () => {
    try {
      const parsed = typeof formData.content === 'string' && formData.content
        ? JSON.parse(formData.content)
        : null;
      
      // Check if it's the new format with questions array
      if (parsed?.questions && Array.isArray(parsed.questions)) {
        return parsed;
      }
      
      // Convert old format to new format
      if (parsed?.question) {
        return {
          passage: parsed.passage || '',
          questions: [{
            id: Date.now().toString(),
            question_text: parsed.question,
            options: parsed.options || ['', '', '', ''],
            correct_index: parsed.correct_index || 0
          }]
        };
      }
      
      // Default new format
      return {
        passage: '',
        questions: [{
          id: Date.now().toString(),
          question_text: '',
          options: ['', '', '', ''],
          correct_index: 0
        }]
      };
    } catch {
      return {
        passage: '',
        questions: [{
          id: Date.now().toString(),
          question_text: '',
          options: ['', '', '', ''],
          correct_index: 0
        }]
      };
    }
  };

  const [contentData, setContentData] = useState(parseContent());

  useEffect(() => {
    onChange({
      ...formData,
      content: JSON.stringify(contentData),
    });
  }, [contentData]);

  const handlePassageChange = (value: string) => {
    setContentData({
      ...contentData,
      passage: value,
    });
  };

  const handleQuestionTextChange = (questionId: string, value: string) => {
    setContentData({
      ...contentData,
      questions: contentData.questions.map((q: MCQuestion) =>
        q.id === questionId ? { ...q, question_text: value } : q
      ),
    });
  };

  const handleOptionChange = (questionId: string, optionIndex: number, value: string) => {
    setContentData({
      ...contentData,
      questions: contentData.questions.map((q: MCQuestion) =>
        q.id === questionId
          ? {
              ...q,
              options: q.options.map((opt, idx) => (idx === optionIndex ? value : opt)),
            }
          : q
      ),
    });
  };

  const handleCorrectIndexChange = (questionId: string, correctIndex: number) => {
    setContentData({
      ...contentData,
      questions: contentData.questions.map((q: MCQuestion) =>
        q.id === questionId ? { ...q, correct_index: correctIndex } : q
      ),
    });
  };

  const addQuestion = () => {
    setContentData({
      ...contentData,
      questions: [
        ...contentData.questions,
        {
          id: Date.now().toString(),
          question_text: '',
          options: ['', '', '', ''],
          correct_index: 0,
        },
      ],
    });
  };

  const removeQuestion = (questionId: string) => {
    if (contentData.questions.length === 1) {
      alert('Phải có ít nhất 1 câu hỏi!');
      return;
    }
    setContentData({
      ...contentData,
      questions: contentData.questions.filter((q: MCQuestion) => q.id !== questionId),
    });
  };

  const addOption = (questionId: string) => {
    setContentData({
      ...contentData,
      questions: contentData.questions.map((q: MCQuestion) =>
        q.id === questionId
          ? {
              ...q,
              options: [...q.options, ''],
            }
          : q
      ),
    });
  };

  const removeOption = (questionId: string, optionIndex: number) => {
    setContentData({
      ...contentData,
      questions: contentData.questions.map((q: MCQuestion) => {
        if (q.id === questionId) {
          // Ensure at least 2 options
          if (q.options.length <= 2) {
            alert('Phải có ít nhất 2 đáp án!');
            return q;
          }

          const newOptions = q.options.filter((_, i) => i !== optionIndex);
          let newCorrectIndex = q.correct_index;

          // Adjust correct_index if the removed option was selected
          if (optionIndex === q.correct_index) {
            newCorrectIndex = 0;
          } else if (optionIndex < q.correct_index) {
            newCorrectIndex = q.correct_index - 1;
          }

          return {
            ...q,
            options: newOptions,
            correct_index: newCorrectIndex,
          };
        }
        return q;
      }),
    });
  };

  // Generate option labels dynamically (A, B, C, D, E, F, ...)
  const generateOptionLabels = (count: number): string[] => {
    const labels: string[] = [];
    for (let i = 0; i < count; i++) {
      labels.push(String.fromCharCode(65 + i)); // 65 is 'A'
    }
    return labels;
  };

  return (
    <FormBox>
      <h5>📚 Nội dung câu hỏi - Đọc đoạn văn và trả lời câu hỏi TRẮC NGHIỆM</h5>
      
      <FormGroup>
        <Label>Đoạn văn: *</Label>
        <Input
          type="textarea"
          value={contentData.passage || ''}
          onChange={(e) => handlePassageChange(e.target.value)}
          placeholder="Nhập đoạn văn để học sinh đọc...

Ví dụ: Lan likes reading books. Every weekend she goes to the library. She spends about 2 hours there."
          rows={5}
        />
      </FormGroup>

      <div style={{ 
        background: '#d1ecf1', 
        border: '1px solid #bee5eb',
        borderRadius: '4px',
        padding: '10px',
        marginBottom: '15px'
      }}>
        <strong>💡 Hướng dẫn:</strong>
        <ul style={{ marginBottom: 0, paddingLeft: '20px', fontSize: '14px' }}>
          <li>Thêm nhiều câu hỏi trắc nghiệm liên quan đến đoạn văn</li>
          <li>Mỗi câu hỏi có tối thiểu 2 đáp án, có thể thêm đáp án (E, F, ...)</li>
          <li>Chọn radio button để đánh dấu đáp án đúng</li>
        </ul>
      </div>

      {/* Questions Section */}
      <div style={{ marginBottom: '15px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <Label style={{ marginBottom: 0, fontWeight: 'bold', fontSize: '16px' }}>
            📝 Câu hỏi trắc nghiệm ({contentData.questions.length})
          </Label>
          <AddButton type="button" onClick={addQuestion}>+ Thêm câu hỏi</AddButton>
        </div>

        {contentData.questions.map((question: MCQuestion, qIndex: number) => (
          <QuestionCard key={question.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <strong style={{ color: '#20c997' }}>Câu hỏi {qIndex + 1}</strong>
              {contentData.questions.length > 1 && (
                <RemoveButton type="button" onClick={() => removeQuestion(question.id)}>
                  🗑️ Xóa câu hỏi
                </RemoveButton>
              )}
            </div>

            <FormGroup>
              <Label>Nội dung câu hỏi: *</Label>
              <Input
                type="text"
                value={question.question_text}
                onChange={(e) => handleQuestionTextChange(question.id, e.target.value)}
                placeholder="Ví dụ: Where does Lan go every weekend?"
              />
            </FormGroup>

            <FormGroup>
              <Label>Các đáp án:</Label>
              {question.options.map((option: string, optIndex: number) => {
                const optionLabels = generateOptionLabels(question.options.length);
                return (
                  <OptionRow key={optIndex}>
                    <OptionBadge>{optionLabels[optIndex]}</OptionBadge>
                    <Input
                      type="text"
                      value={option}
                      onChange={(e) => handleOptionChange(question.id, optIndex, e.target.value)}
                      placeholder={`Đáp án ${optionLabels[optIndex]}...`}
                      style={{ flex: 1 }}
                    />
                    <RadioLabel>
                      <input
                        type="radio"
                        name={`correct_answer_${question.id}`}
                        checked={question.correct_index === optIndex}
                        onChange={() => handleCorrectIndexChange(question.id, optIndex)}
                      />
                      <span style={{ fontSize: '14px', color: '#666' }}>Đúng</span>
                    </RadioLabel>
                    {question.options.length > 2 && (
                      <RemoveOptionButton type="button" onClick={() => removeOption(question.id, optIndex)}>
                        ✕ Xóa
                      </RemoveOptionButton>
                    )}
                  </OptionRow>
                );
              })}
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <AddOptionButton type="button" onClick={() => addOption(question.id)}>
                  + Thêm đáp án
                </AddOptionButton>
              </div>
              <small className="form-text text-muted">
                ✓ Chọn radio button để đánh dấu đáp án đúng
              </small>
            </FormGroup>
          </QuestionCard>
        ))}
      </div>

      {/* Preview */}
      <div style={{ 
        background: '#d1f2eb', 
        padding: '15px', 
        borderRadius: '4px',
        marginTop: '15px'
      }}>
        <strong style={{ fontSize: '13px', color: '#00695c' }}>📋 Xem trước:</strong>
        
        {contentData.passage ? (
          <PassageSection>
            {contentData.passage}
          </PassageSection>
        ) : (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            color: '#999',
            fontStyle: 'italic'
          }}>
            (chưa có đoạn văn)
          </div>
        )}
        
        {contentData.questions.length > 0 && (
          <div style={{ marginTop: '15px' }}>
            {contentData.questions.map((q: MCQuestion, idx: number) => (
              <div key={q.id} style={{ 
                marginTop: idx > 0 ? '15px' : '0',
                padding: '12px',
                background: 'white',
                borderRadius: '4px',
                borderLeft: '4px solid #20c997'
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>❓ Câu {idx + 1}:</strong> {q.question_text || '(chưa nhập)'}
                </div>
                {q.options.some((opt: string) => opt.trim()) && (
                  <div style={{ paddingLeft: '15px', fontSize: '14px' }}>
                    {q.options.map((opt: string, i: number) => {
                      const qOptionLabels = generateOptionLabels(q.options.length);
                      return (
                        opt.trim() && (
                          <div 
                            key={i} 
                            style={{ 
                              marginBottom: '4px',
                              fontWeight: q.correct_index === i ? 'bold' : 'normal',
                              color: q.correct_index === i ? '#20c997' : 'inherit'
                            }}
                          >
                            {qOptionLabels[i]}. {opt}
                            {q.correct_index === i && ' ✓'}
                          </div>
                        )
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Debug JSON */}
      <details style={{ marginTop: '15px' }}>
        <summary style={{ cursor: 'pointer', color: '#666', fontSize: '14px' }}>
          🔍 Xem JSON (Debug)
        </summary>
        <pre style={{ 
          background: '#f5f5f5', 
          padding: '10px', 
          borderRadius: '4px',
          fontSize: '12px',
          maxHeight: '200px',
          overflow: 'auto',
          marginTop: '10px'
        }}>
          {JSON.stringify(contentData, null, 2)}
        </pre>
      </details>
    </FormBox>
  );
};

export default ReadingMCQForm;
